import { getIdToken } from "../../utils/firebaseAuth";
import axios from "axios";
import connectToDb from "../../utils/mongodb";
import VoiceImage from "../../components/VoiceImage";
import { useInfiniteQuery } from "react-query";
import millify from "millify";
import { useState, useEffect, useRef } from "react";
import useAuthModal from "../../hooks/useAuthModal";
import { useAuth } from "../../context/AuthProvider";
import { toast } from "react-hot-toast";
import { auth } from "../../utils/firebaseAdmin";
import { MdEdit } from "react-icons/md";
import { DropdownMenuItem } from "@jammable/ui-components/DropdownMenu";
import { BsThreeDots } from "react-icons/bs";
import Dropdown from "../../components/dropdown/Dropdown";
import mongoose from "mongoose";
import {
  FaHeart,
  FaLock,
  FaPlus,
  FaRegHeart,
  FaShare,
  FaTrash,
} from "react-icons/fa";
import Link from "next/link";
import EditCollectionModal from "../../components/collection/modals/EditCollectionModal";
import { useModal } from "../../context/ModalContext";
import DeleteCollectionModal from "../../components/collection/modals/DeleteCollectionModal";
import { Spinner } from "@jammable/ui-components/Loader";
import { BASE_URL } from "../../utils/tools";
import NotFound from "../../components/NotFound";
import { NextSeo } from "next-seo";
import Head from "next/head";
import { useRouter } from "next/router";
import AddVoiceModal from "../../components/modals/AddVoiceModal";
import {
  JamVoiceCard,
  JamCardSkeleton,
  JamVoiceCardSkeleton,
} from "../../components/home/JamCards";

// The hero takes the collection image's dark palette colour (as the mockup's
// per-collection colour block); the schema defaults missing swatches to
// [0,0,0], so treat black as "no colour" and fall back to brand purple.
const heroColor = (image) => {
  const palette = image?.colorPalette;
  for (const key of ["DarkVibrant", "DarkMuted"]) {
    const rgb = palette?.[key]?.rgb;
    if (rgb?.length === 3 && rgb.some((v) => v > 0)) {
      return `rgb(${rgb.join(",")})`;
    }
  }
  return "#782DC8";
};

// The page size /api/collection/getCollectionVoices serves.
const VOICES_PER_PAGE = 24;

// What JamVoiceCard reads, and nothing else: the API's page carries the whole
// MODEL_PROJECTION (descriptions, demos, every palette swatch), which would
// be most of the HTML if it went into page props as-is.
const toCardModel = (model) => ({
  _id: model._id,
  urlParam: model.urlParam,
  artistname: model.artistname,
  uses: model.uses,
  image: {
    url: model.image?.url,
    colorPalette: { LightMuted: model.image?.colorPalette?.LightMuted },
  },
  user: { username: model.user?.username },
});

const CollectionPage = ({
  collection,
  page = 1,
  initialVoices,
  initialVoicesSignedIn,
  error,
}) => {
  const { user } = useAuth();

  const [isLiked, setIsLiked] = useState(collection?.isLiked);
  const [likes, setLikes] = useState(collection?.likes);
  const [liking, setLiking] = useState(false);
  const { checkAuthAndExecute } = useAuthModal();
  const { openModal, closeModal } = useModal();

  const getCollectionVoices = async (collectionId, page) => {
    const token = await getIdToken();
    const res = await axios.get(`/api/collection/getCollectionVoices`, {
      params: {
        collectionId,
        page,
        sortBy,
      },
      headers: {
        Authorization: token,
      },
    });
    return res.data;
  };
  const [sortBy, setSortBy] = useState("hot");

  const voices = useInfiniteQuery({
    queryKey: ["collectionVoices", collection._id, sortBy, page],
    queryFn: ({ pageParam = page }) =>
      getCollectionVoices(collection._id, pageParam),
    getNextPageParam: (lastPage, pages) => {
      // modelsCount includes removed and private voices (5,022 submitted
      // collections count more than they show), so a short page is the end.
      if ((lastPage?.models?.length ?? 0) < lastPage.limit) return null;
      const maxPages = Math.ceil(collection?.modelsCount / lastPage.limit);
      const nextPage = page + pages.length;
      return nextPage <= maxPages ? nextPage : null;
    },
    // This page's voices come from getServerSideProps so they are links in
    // the HTML Google fetches; client-rendered, a collection was a title and
    // ~640 characters of text. Not refetched on mount: that would ask the
    // API for the same 24 voices again. Only the default sort is seeded.
    initialData:
      sortBy === "hot" && initialVoices
        ? {
            pages: [{ models: initialVoices, limit: VOICES_PER_PAGE }],
            pageParams: [page],
          }
        : undefined,
    refetchOnMount: false,
  });

  // An expired token cookie renders the server page signed out, which leaves
  // out the owner's private voices. Fetch again once auth knows who it is.
  const { refetch } = voices;
  const ownerSeenSignedOut =
    !!initialVoices &&
    !initialVoicesSignedIn &&
    !!user &&
    collection?.userId === user?.firebaseUid;
  useEffect(() => {
    if (ownerSeenSignedOut) refetch();
  }, [ownerSeenSignedOut, refetch]);

  // Infinite scroll off a sentinel below the grid, fetched 1500px early;
  // while more pages exist it is a screen tall so the footer stays off
  // screen. The old handler fired 800px from the document's end, with the
  // phone footer already in view, and each page pushed it down: 0.28-0.6
  // CLS on a scroll to the end (2026-10-10 probe) - this template's
  // "poor" CLS group in Search Console (0.31).
  const loadMoreRef = useRef(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isError } = voices;
  useEffect(() => {
    const el = loadMoreRef.current;
    // After a failed page, stop: re-observing would refetch in a loop.
    if (!el || !hasNextPage || isError) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "0px 0px 1500px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isError]);

  const likeCollection = async () => {
    if (!checkAuthAndExecute()) return false;
    setLiking(true);
    if (isLiked) {
      setLikes(likes - 1);
    } else {
      setLikes(likes + 1);
    }
    setIsLiked(!isLiked);
    const token = await getIdToken();
    if (!token) {
      return;
      setLiking(false);
    }
    axios
      .post(
        `/api/collection/likeCollection`,
        { collectionId: collection?._id },
        { headers: { Authorization: token } },
      )
      .then((res) => {
        toast.success(res?.data.message);
        setLiking(false);
      })
      .catch((err) => {
        toast.success("Something went wrong. Please try again");
      });
  };

  const [randomCollabLoading, setRandomCollabLoading] = useState(false);

  const pageSuffix = page > 1 ? ` - Page ${page}` : "";
  const title = `${collection?.title} AI Voices (${collection?.modelsCount}+)${pageSuffix} | Jammable`;
  const description = `${collection?.title} AI voice, cover and song generator. Create AI voice covers with ${collection?.modelsCount}+ ${collection?.title} AI voices using advanced AI voice technology.`;
  const url = `${BASE_URL}/collection/${collection?._id}`;
  // Pages past the first are their own URLs (see getServerSideProps).
  const pageHref = (n) =>
    n > 1
      ? `/collection/${collection?._id}?page=${n}`
      : `/collection/${collection?._id}`;
  const canonical = `${BASE_URL}${pageHref(page)}`;
  const nextPage = page + (voices?.data?.pages?.length || 1);
  const router = useRouter();

  const isOwner = !!user && collection?.userId === user?.firebaseUid;
  const models = (voices?.data?.pages || []).flatMap(
    (page) => page?.models || [],
  );

  const startGroupCover = () => {
    if (randomCollabLoading) return;
    setRandomCollabLoading(true);
    axios
      .get(`/api/collection/randomDuetVoices?collectionId=${collection._id}`)
      .then((res) => {
        setTimeout(() => {
          setRandomCollabLoading(false);
        }, 1000);
        const voiceIds = res.data.voiceIds;
        router.push(`/create?voices=${voiceIds.join(",")}`);
      })
      .catch((err) => {
        toast.error("Something went wrong. Please try again");
        setRandomCollabLoading(false);
      });
  };

  const openAddVoice = () =>
    openModal("add-voice-modal", <AddVoiceModal collection={collection} />);

  const menuItem =
    "tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-3xl tw-p-2 tw-pl-3 tw-font-extrabold";

  return (
    <div className="tw-bg-white tw-text-jam-ink">
      <NextSeo
        title={title}
        description={description}
        canonical={canonical}
        // Not noindexed for being small. That rule (2026-10-10, < 3 voices)
        // would have dropped 273 collections that earned 4,176 clicks in the
        // previous 90 days (23% of collection clicks; collections are the
        // only page type growing in search). Small collections are still
        // left out of the sitemap (utils/sitemap.ts), which doesn't deindex
        // a page that already earns traffic.
        openGraph={{
          title: title,
          description: description,
          url: canonical,

          images: [
            {
              url: collection?.image?.url,
              width: 900,
              height: 900,
              alt: `${collection?.title} AI Voice Collection`,
            },
          ],
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.jammable.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "AI Voice Library",
                  item: "https://www.jammable.com/models",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: collection?.title,
                  item: url,
                },
              ],
            }),
          }}
        />
      </Head>
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-6 tw-px-4 tw-pb-28 tw-pt-2 md:tw-px-8">
        <section
          style={{ background: heroColor(collection?.image) }}
          className="tw-flex tw-flex-col tw-gap-5 tw-rounded-[34px] tw-p-5 tw-text-white md:tw-min-h-[240px] md:tw-flex-row md:tw-items-end md:tw-gap-6 md:tw-rounded-[44px] md:tw-p-9"
        >
          <div className="tw-flex tw-min-w-0 tw-flex-grow tw-items-end tw-gap-5">
            {collection?.image?.url && (
              <div className="tw-relative tw-hidden tw-h-[132px] tw-w-[132px] tw-flex-shrink-0 tw-overflow-hidden tw-rounded-[30px] tw-border-4 tw-border-solid tw-border-white/30 md:tw-block">
                <VoiceImage
                  model={{ image: collection.image, title: collection.title }}
                  fill
                  sizes="132px"
                  priority
                  className="tw-object-cover"
                />
              </div>
            )}
            <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2.5">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <span className="tw-rounded-full tw-bg-white tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold tw-text-jam-ink">
                  Collection · {millify(collection?.modelsCount ?? 0)}{" "}
                  {collection?.modelsCount === 1 ? "voice" : "voices"}
                </span>
                {collection.isPrivate && (
                  <span className="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-white/20 tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold">
                    <FaLock size={10} />
                    Private
                  </span>
                )}
              </div>
              <h1 className="jam-display tw-m-0 tw-break-words tw-text-[40px] tw-font-extrabold tw-leading-none md:tw-text-[60px]">
                {collection.autoGeneratedId
                  ? `AI ${collection.title} Voices`
                  : `${collection.title}`}
              </h1>
              <div className="tw-text-[13px] tw-font-bold tw-text-white/85 md:tw-text-[15px]">
                {collection?.autoGeneratedId ? (
                  "Auto-generated collection"
                ) : (
                  <Link
                    href={`/@${collection?.user?.username}`}
                    className="tw-text-white tw-no-underline hover:tw-underline"
                  >
                    by @{collection?.user?.username}
                  </Link>
                )}
                {" · "}
                {millify(likes || 0)} {likes === 1 ? "like" : "likes"}
              </div>
              {(collection.autoGeneratedId || collection.description) && (
                <div className="tw-max-w-[640px] tw-text-[13px] tw-font-semibold tw-leading-normal tw-text-white/85 md:tw-text-sm">
                  {collection.autoGeneratedId
                    ? `This AI ${collection.title} voice collection was auto-generated. It contains ${collection.modelsCount} AI ${collection.title} voices which can be used for AI covers.`
                    : collection.description}
                </div>
              )}
            </div>
          </div>

          <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2">
            <button
              type="button"
              onClick={likeCollection}
              aria-pressed={!!isLiked}
              className="tw-flex tw-h-12 tw-flex-grow tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-white tw-px-5 tw-text-sm tw-font-extrabold tw-text-jam-ink md:tw-h-[50px] md:tw-flex-grow-0 md:tw-text-[15px]"
            >
              {isLiked ? (
                <FaHeart size={15} className="tw-text-jam-purple" />
              ) : (
                <FaRegHeart size={15} />
              )}
              {isLiked ? "Liked" : "Like"}
            </button>
            {collection?.modelsCount > 0 && (
              <button
                type="button"
                onClick={startGroupCover}
                disabled={randomCollabLoading}
                className="tw-flex tw-h-12 tw-flex-grow tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-full tw-border-0 tw-bg-jam-lime tw-px-5 tw-text-sm tw-font-extrabold tw-text-jam-ink disabled:tw-opacity-70 md:tw-h-[50px] md:tw-flex-grow-0 md:tw-text-[15px]"
              >
                {randomCollabLoading && (
                  <Spinner size={4} className="tw-border-3 tw-border-jam-ink" />
                )}
                <span className="md:tw-hidden">Group cover</span>
                <span className="tw-hidden md:tw-inline">
                  Make a group cover
                </span>
              </button>
            )}
            {isOwner && (
              <button
                type="button"
                aria-label="Add a voice"
                onClick={openAddVoice}
                className="tw-flex tw-h-12 tw-w-12 tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white/20 tw-text-white md:tw-h-[50px] md:tw-w-[50px]"
              >
                <FaPlus size={16} />
              </button>
            )}
            <Dropdown
              dropdownItems={
                <>
                  <DropdownMenuItem
                    onClick={async () => {
                      navigator.clipboard.writeText(url);
                      toast.success("Copied to clipboard!");
                    }}
                    className={`${menuItem} hover:tw-bg-jam-lav`}
                  >
                    <FaShare />
                    Share Collection
                  </DropdownMenuItem>
                  {isOwner && (
                    <DropdownMenuItem
                      onClick={() => {
                        openModal(
                          "edit-collection-modal",
                          <EditCollectionModal
                            collection={collection}
                            reroute={true}
                          />,
                        );
                      }}
                      className={`${menuItem} hover:tw-bg-jam-lav`}
                    >
                      <MdEdit />
                      Edit Collection
                    </DropdownMenuItem>
                  )}

                  {isOwner && (
                    <DropdownMenuItem
                      onClick={() => {
                        openModal(
                          "delete-conversion-modal",
                          <DeleteCollectionModal
                            collection={collection}
                            closeModal={() =>
                              closeModal("delete-collection-modal")
                            }
                          />,
                        );
                      }}
                      className={`${menuItem} tw-text-red-500 hover:tw-bg-red-100`}
                    >
                      <FaTrash />
                      <span>Delete Collection</span>
                    </DropdownMenuItem>
                  )}
                </>
              }
            >
              <div
                aria-label="More"
                className="tw-flex tw-h-12 tw-w-12 tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-white/20 tw-text-white md:tw-h-[50px] md:tw-w-[50px]"
              >
                <BsThreeDots size={20} />
              </div>
            </Dropdown>
          </div>
        </section>

        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
          <span className="tw-text-sm tw-font-extrabold tw-text-jam-muted">
            {page > 1 && (
              <>
                <Link
                  href={pageHref(page - 1)}
                  className="tw-text-jam-purple tw-no-underline hover:tw-underline"
                >
                  ← Previous
                </Link>
                {` · Page ${page} · `}
              </>
            )}
            {millify(collection?.modelsCount ?? 0)}{" "}
            {collection.modelsCount === 1 ? "voice" : "voices"}
          </span>
          <div className="tw-flex tw-gap-1 tw-rounded-full tw-bg-jam-lav tw-p-1 tw-text-[13px] tw-font-extrabold">
            {[
              ["hot", "Most used"],
              ["new", "Newest"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={sortBy === key}
                onClick={() => setSortBy(key)}
                className={`tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-3.5 tw-py-2 ${
                  sortBy === key
                    ? "tw-bg-white tw-text-jam-purple"
                    : "tw-bg-transparent tw-text-jam-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {!voices?.data?.pages[0].models?.length && !voices?.isLoading && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4">
            <NotFound
              title="No voices found..."
              description={
                isOwner
                  ? "Get started by adding a voice to your collection!"
                  : "This collection has no voices yet."
              }
              className="tw-mt-6"
            />
            {isOwner && (
              <button
                type="button"
                onClick={openAddVoice}
                className="tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-purple tw-px-6 tw-py-3.5 tw-text-[15px] tw-font-extrabold tw-text-white"
              >
                Add a voice
              </button>
            )}
          </div>
        )}

        <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 md:tw-grid-cols-6 md:tw-gap-3.5">
          {voices?.isLoading
            ? [...Array(12)].map((_, i) => <JamVoiceCardSkeleton key={i} />)
            : models.map((model) => (
                <JamVoiceCard key={model._id} model={model} />
              ))}
          {/* The next page arrives where it will sit, like the first. */}
          {voices.isFetchingNextPage &&
            [...Array(6)].map((_, i) => (
              <JamVoiceCardSkeleton key={`next-${i}`} />
            ))}
        </div>
        {/* The link is how a crawler (or a visitor without JS) reaches voices
            past the first page: 1,222 of the 15,844 submitted collections
            have more than 24 to show (2026-10-11). With JS the sentinel
            fetches the next page 1500px before anyone reaches it. */}
        {voices.hasNextPage && !voices.isError && (
          <div
            ref={loadMoreRef}
            className="tw-flex tw-min-h-[100vh] tw-items-start tw-justify-center"
          >
            <Link
              href={pageHref(nextPage)}
              className="tw-text-sm tw-font-extrabold tw-text-jam-muted tw-no-underline hover:tw-text-jam-purple"
            >
              Next page →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionPage;
export async function getServerSideProps(context) {
  const { collectionid } = context.params;
  // ?page=N is the server-rendered page N of the default sort, linked from
  // the page before it. Anything unparseable is page 1.
  const page = Math.max(1, parseInt(context.query.page, 10) || 1);

  await connectToDb();
  const token = context?.req?.cookies?.token;

  let firebaseUid;
  try {
    const decodedToken = await auth.verifyIdToken(token);
    firebaseUid = decodedToken.uid;
  } catch (error) {}

  try {
    let collection;
    try {
      collection = await getCollectionFromMongoId(collectionid, firebaseUid);
    } catch (error) {}

    if (!collection) {
      return {
        notFound: true,
      };
    }

    if (
      page > 1 &&
      page > Math.ceil((collection.modelsCount || 0) / VOICES_PER_PAGE)
    ) {
      return { notFound: true };
    }

    // The same call, sort ("Most used") and page size the client's first
    // fetch makes, so react-query can take it as that page. A failure here
    // must never fail the page, and the aggregation $lookups every voice in
    // the collection (13k in the largest), so it has a time budget: past it
    // the page renders as before and the browser fetches the voices.
    let initialVoices = null;
    try {
      const lookup = (async () => {
        const { getModelsFromCollection } = await import(
          "../api/collection/getCollectionVoices"
        );
        return getModelsFromCollection(
          collectionid,
          { usesLast24Hours: -1, uses: -1 },
          (page - 1) * VOICES_PER_PAGE,
          VOICES_PER_PAGE,
          page,
          firebaseUid,
          collection.autoGeneratedId ?? null,
        );
      })();
      let timer;
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(
          () => reject(new Error("timed out after 1500ms")),
          1500,
        );
      });
      try {
        initialVoices = (await Promise.race([lookup, timeout])).map(
          toCardModel,
        );
      } finally {
        clearTimeout(timer);
        lookup.catch(() => {}); // a late rejection must not go unhandled
      }
    } catch (error) {
      console.error("collection page voices failed:", collectionid, error);
    }

    // A page past the end (removed or private voices still count in
    // modelsCount) has nothing on it.
    if (page > 1 && initialVoices && initialVoices.length === 0) {
      return { notFound: true };
    }

    return {
      props: {
        collection: JSON.parse(JSON.stringify(collection)),
        page,
        initialVoices: initialVoices
          ? JSON.parse(JSON.stringify(initialVoices))
          : null,
        initialVoicesSignedIn: !!firebaseUid,
      },
    };
  } catch (error) {
    console.error("Error fetching collection:", error);
    return {
      props: {
        error: "Failed to fetch collection",
      },
    };
  }
}

const getCollectionFromMongoId = async (collectionid, firebaseUid) => {
  const Collection = (await import("../../models/Collection")).default;
  let collection = await Collection.aggregate([
    { $match: { _id: new mongoose.Types.ObjectId(collectionid) } },
    {
      $addFields: {
        isLiked: {
          $in: [firebaseUid, { $ifNull: ["$likedBy", []] }],
        },
      },
    },
    {
      $project: {
        title: 1,
        description: 1,
        image: 1,
        autoGeneratedId: 1,
        likes: 1,
        isLiked: 1,
        modelsCount: 1,
        user: 1,
        userId: 1,
        deleted: 1,
        isPrivate: 1,
        models: 1,
      },
    },
  ]);
  if (collection[0].isPrivate && collection[0].userId !== firebaseUid) {
    return null;
  }

  if (!collection[0]) return null;
  if (collection[0].deleted) return null;
  return collection[0];
};
